<script setup lang="ts">
    import { computed, ref, toRef } from 'vue'
    import { MonthPicker, MonthPickerInput } from 'vue-month-picker'

    import type { ProjectScheduledLifecycleSetterViewModel } from "@Quest/Projects/Components/ProjectScheduledLifecycleSetter"
    import type { MonthPickerDate } from "@common/Components/MonthPickerDialog"
    import { MonthYearHelpers } from "@common/types"

    // props
    const props = defineProps<{
        viewModel: ProjectScheduledLifecycleSetterViewModel
    }>()

    // emits
    const emit = defineEmits<{
        (e: 'confirm', projectId: string, date: any): void
    }>()

    // vm shortcut
    const vm = toRef(props, 'viewModel')

    // state
    const opened = computed(() => vm.value?.opened ?? false)

    const startDate = computed<MonthPickerDate | null>({
        get: () => vm.value.startDate,
        set: v => vm.value.startDate = v
    })

    const isStartDateSet = computed(() =>
        !!(startDate.value && startDate.value.monthIndex && startDate.value.year)
    )

    const isStartDateValid = computed(() =>
        (window as any).MonthPickerDate?.isValid
            ? (window as any).MonthPickerDate.isValid(startDate.value, vm.value.minStartDate, vm.value.maxStartDate)
            : true
    )

    // local UI state
    const isStartDateMonthPickerVisible = ref(false)

    // label formatting
    const startDateLabel = computed<string | null>(() =>
    {
        if (!isStartDateSet.value) return null

        const date = new Date(
            startDate.value!.year,
            startDate.value!.monthIndex - 1,
            1
        )

        return new Intl.DateTimeFormat('en-US', {
            month: 'long',
            year: 'numeric'
        }).format(date)
    })

    // actions
    function onStartDateChange(date: MonthPickerDate)
    {
        startDate.value = date
    }

    function confirm()
    {
        if (isStartDateSet.value && isStartDateValid.value)
        {
            const date = MonthYearHelpers.build(
                vm.value.startDate.year,
                vm.value.startDate.monthIndex
            )

            emit('confirm', vm.value.projectId, date)
        }
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Change Project Lifecycle to Scheduled</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <p>
                            You are about to change the project lifecycle to the <strong>Scheduled</strong> stage.
                            Please ensure the start date is set and valid.
                        </p>

                        <div class="form-group">
                            <label>Start date:</label>
                            <div class="input-group">
                                <input :value="startDateLabel" placeholder="Select a start date" type="text" class="form-control pointer bg-white" readonly @click="isStartDateMonthPickerVisible = !isStartDateMonthPickerVisible" />
                                <div class="input-group-append">
                                    <button class="btn btn-outline-secondary" type="button" @click="isStartDateMonthPickerVisible = !isStartDateMonthPickerVisible">
                                        <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                                    </button>
                                </div>
                            </div>
                            <p class="text-danger" v-if="!isStartDateSet || !isStartDateValid">
                                A start date is required and cannot be in the past.
                            </p>
                            <month-picker v-show="isStartDateMonthPickerVisible"
                                          @change="onStartDateChange"
                                          :min-date="vm.minStartDate"
                                          :max-date="vm.maxStartDate"
                                          :default-month="vm.defaultMonth"
                                          :default-year="vm.defaultYear" />
                        </div>

                    </div>
                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="button" @click="confirm" class="btn btn-primary" :disabled="!isStartDateSet || !isStartDateValid">Confirm</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

